/**
 * @Author: v_jppjpeng
 * @Date: 2023-10-30 15:13:06
 * @Explain: 下拉菜单
 */
import { intlGet } from '@/locales';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { PopupVisibleChangeContext } from 'tdesign-react';
import { Loading, Popup } from 'tdesign-react';
// utils
import { getDefaultValueByType } from '@/utils/common/dataConversion';
import { isArrayLen } from '@/utils/common/dataJudgment';
import { onSelectRenderText } from '../const';
// type
import type { SelectProps } from '../data.d';
import { InputSizeEnum } from '../data.d';

import GroupList from '../groupList';
import OptionList from '../optionList';
import SelectCustom from '../select/custom';

type PropsType = {
  children?: ReactNode;
  allowScroll?: boolean;
} & SelectProps;

function CustomSelect(props: PropsType) {
  const {
    className,
    children,
    listHeader,
    listMaxHeight,
    footer,
    value,
    placeholder = intlGet('COMMON.PLEASE.SELECT'),
    size = InputSizeEnum.M,
    allOption,
    multiple = false,
    isExpanded = false,
    defaultExpanded = false,
    searchable,
    searchPlaceholder,
    disabled,
    loading = false,
    loadingText,
    shadow = true,
    shadowIsClose = true,
    clearable,
    autoWidth = false,
    matchButtonWidth = true,
    borderless = false,
    isStopPropagation = false,
    suspendedText,
    isDisplayTextStyle,
    displayText,
    displayTextType,
    prefixIcon,
    prefixIconName,
    suffixIcon,
    suffixIconName,
    emptyText,

    isGroup = false,
    groupKey = 'group',
    groupSortType,
    groupTitleJson,
    groupDividingLine = false,
    displayTextKey = 'label',
    displayOptions = [],
    options,
    customOption,

    // popup - props
    trigger = 'click',
    placement = 'bottom-left',
    width = 'auto',
    maxColumnWidth,
    minColumnWidth,
    overlayClassName,
    overlayInnerStyle,
    overlayStyle,
    popupProps,

    // tag - input - props
    minCollapsedNum,
    collapsedItems,
    valueDisplay,

    // 允许页面滚动
    allowScroll = false,
    onVisibleChange,
    onClick,
    onChange,
    onClose,
    onClearable,
    attach=''
  } = props;
  const popupRef = useRef(null);

  const dropdownInputRef = useRef<HTMLDivElement>(null);
  // 下拉项内容Ref
  const contentRef = useRef<any>(null);
  // 同步存储visible情况
  const visibleRef = useRef<any>(null);

  const [visible, setVisible] = useState(false);
  const [popupWidth, setPopupWidth] = useState<any>(width);

  /** 文本渲染逻辑 */
  const valueText = useMemo(
    () =>
      isArrayLen(options)
        ? onSelectRenderText(options, value, { displayOptions })
        : displayOptions,
    [value, options],
  );

  // 监听 - 执行关闭
  // const onCloseListener = (event?: any) => {
  //   try {
  //     let flag = true;

  //     const domArr = [
  //       'dropdown-search-box',
  //       't-input',
  //       't-input__inner',
  //       'td-group-list',
  //       'td-scroll-list',
  //       'select-custom-components',
  //       // 'dropdown-footer',
  //       // 'go-page-button',
  //       // 't-link',
  //     ];

  //     for (const item of domArr) {
  //       if (event?.target?.closest(`.${item}`)) {
  //         flag = false;
  //         break;
  //       }
  //     }
  //     if (flag && visible) {
  //       setVisible(false);
  //       onClose?.();
  //     }
  //   } catch (e) {
  //     console.error('onClose =>>', e);
  //   }
  // };

  // 监听滚动，点击其他区域
  // useEffect(() => {
  //   // window.addEventListener('wheel', onCloseListener);
  //   window.addEventListener('mousedown', onCloseListener);
  //   return () => {
  //     // window.removeEventListener('wheel', onCloseListener);
  //     window.removeEventListener('mousedown', onCloseListener);
  //   };
  // }, [visible]);

  const customPopupProps = useMemo(() => {
    const config: any = {
      visible,
      trigger,
      placement,
      disabled,
    };
    // 当hover时，使用组件内置开启隐藏
    if (trigger === 'hover') {
      delete config.visible;
    }
    if(attach){
      config.attach=attach
    }
    return config;
  }, [visible, trigger, placement, disabled]);

  const shadowRender = useMemo(
    () =>
      visible &&
      shadow && (
        <div
          className="popup-shadow"
          onClick={() => {
            setVisible(false);
            onClose?.();
          }}
        />
      ),
    [visible, shadow, shadowIsClose, setVisible, onClose,attach],
  );

  const popupOnVisibleChange = (
    visible: boolean,
    context: PopupVisibleChangeContext,
  ) => {
    if (matchButtonWidth) {
      const width = dropdownInputRef.current?.offsetWidth || 0;

      if (width > 0) {
        setPopupWidth(width);
      }
    }
    onVisibleChange?.(visible, context);
  };

  // 手动更新弹层位置(折叠项展开/收起时)
  const handleCollapseChange = () => {
    setTimeout(() => {
      const popper = popupRef.current?.getPopper?.();
      popper?.update();
    }, 10);
  };

  // 监听点击下拉项范围外 - 执行关闭
  const onCloseListener = (event?: any) => {
    // 组件关闭 或 组件已被卸载，元素已不存在，则无需再向下执行关闭
    if (
      !visibleRef.current ||
      [contentRef?.current, dropdownInputRef?.current].includes(null)
    ) {
      return;
    }

    // 点击事件是否落下下拉项内
    const isClickListDom =
      contentRef?.current && contentRef.current.contains(event.target);
    // 点击事件是否落下触发展开下拉项Dom
    const isClickOpenDom =
      dropdownInputRef?.current &&
      dropdownInputRef?.current?.contains(event.target);

    // 关闭下拉逻辑
    if (!isClickListDom && !isClickOpenDom) {
      popupOnVisibleChange(false, {});
      setVisible(false);
      onClose?.();
    }
  };
  // 阻止滚动
  const preventScroll = (event: any) => event.preventDefault();

  useEffect(() => {
    visibleRef.current = visible;
    window.addEventListener('click', onCloseListener);
    const $layoutBodyDom: any = document.getElementsByClassName(
      'smartlink-layout__body',
    )[0];
    visible &&
      !allowScroll &&
      $layoutBodyDom &&
      $layoutBodyDom.addEventListener('wheel', preventScroll);

    // 监听窗口尺寸或滚动事件更新弹层位置
    if (visible) {
      window.addEventListener('resize', handleCollapseChange);
      window.addEventListener('scroll', handleCollapseChange);
    }
    return () => {
      $layoutBodyDom &&
        !allowScroll &&
        $layoutBodyDom.removeEventListener('wheel', preventScroll);

      window.removeEventListener('resize', handleCollapseChange);
      window.removeEventListener('scroll', handleCollapseChange);
    };
  }, [visible, dropdownInputRef?.current, allowScroll]);
  return (
    <>
      <Popup
        ref={popupRef}
        {...customPopupProps}
        overlayClassName={overlayClassName}
        overlayStyle={{
          ...overlayStyle,
        }}
        overlayInnerStyle={{
          width: popupWidth,
          minWidth: minColumnWidth,
          maxWidth: maxColumnWidth,
          ...overlayInnerStyle,
        }}
        content={
          <div ref={contentRef} onClick={(e) => e?.stopPropagation()}>
            {loading ? (
              <div className="pd-20 text-align-center">
                <Loading size="small" />
              </div>
            ) : (
              <>
                {isGroup ? (
                  <GroupList
                    groupKey={groupKey}
                    groupSortType={groupSortType}
                    groupTitleJson={groupTitleJson}
                    options={options}
                    value={value}
                    searchPlaceholder={searchPlaceholder}
                    size={size}
                    searchable={searchable}
                    multiple={multiple}
                    autofocus={visible}
                    isExpanded={isExpanded}
                    defaultExpanded={defaultExpanded}
                    displayTextKey={displayTextKey}
                    emptyText={emptyText}
                    listHeader={listHeader}
                    groupDividingLine={groupDividingLine}
                    customOption={customOption}
                    onChange={onChange}
                    onClick={(val, item) => {
                      setVisible(false);
                      onChange?.(val, item);
                      onClick?.(val, item);
                    }}
                    handleCollapseChange={handleCollapseChange}
                  />
                ) : (
                  <OptionList
                    searchable={searchable}
                    allOption={allOption}
                    multiple={multiple}
                    autofocus={visible}
                    options={options}
                    value={value}
                    searchPlaceholder={searchPlaceholder}
                    displayTextKey={displayTextKey}
                    emptyText={emptyText}
                    listHeader={listHeader}
                    listMaxHeight={listMaxHeight}
                    customOption={customOption}
                    onClose={() => {
                      setVisible(false);
                      onClose?.();
                    }}
                    onChange={onChange}
                    onClick={(val, item) => {
                      setVisible(false);
                      onChange?.(val, item);
                      onClick?.(val, item);
                    }}
                  />
                )}
                {footer}
              </>
            )}
          </div>
        }
        onVisibleChange={popupOnVisibleChange}
        {...popupProps}
      >
        <span
          ref={dropdownInputRef}
          // 当full的时候才100%，其他情况自适应
          className={classNames('select-click-dom', {
            'width-100': size === InputSizeEnum.FULL,
          })}
          onClick={(e) => {
            if (isStopPropagation) {
              e.stopPropagation();
            }
            if (!disabled) {
              setVisible((val) => !val);
            }
          }}
        >
          {children || (
            <SelectCustom
              className={className}
              suspendedText={suspendedText}
              isDisplayTextStyle={isDisplayTextStyle}
              displayText={displayText}
              displayTextKey={displayTextKey}
              displayTextType={displayTextType}
              valueText={valueText}
              visible={visible}
              value={value}
              loading={loading}
              disabled={disabled}
              clearable={clearable}
              borderless={borderless}
              multiple={multiple}
              placeholder={placeholder}
              size={size}
              autoWidth={autoWidth}
              prefixIcon={prefixIcon}
              prefixIconName={prefixIconName}
              suffixIcon={suffixIcon}
              suffixIconName={suffixIconName}
              minCollapsedNum={minCollapsedNum}
              collapsedItems={collapsedItems}
              valueDisplay={valueDisplay}
              onClearable={() => {
                if (onClearable) {
                  onClearable?.();
                }
                onChange?.(getDefaultValueByType(value), {
                  trigger: 'clear',
                });
              }}
              onTagRemove={({ index }) => {
                const newValue = [...value];
                newValue.splice(index, 1);
                onChange?.(newValue, {
                  trigger: 'tagRemove',
                });
              }}
            />
          )}
        </span>
      </Popup>
      {/* 阴影层 */}
      {shadowRender}
    </>
  );
}

export default CustomSelect;
